import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl, ReauthCtrl } from '../../components/ui';
import { SessionManager } from '../../utils/auth';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';

// ─── Reactions ───
// Server stores word keys (reactions.reaction_type); emoji is presentation only.
export const REACTIONS=[
  {key:"fire",emoji:"🔥",label:"Fire"},{key:"strong",emoji:"💪",label:"Strong"},{key:"clap",emoji:"👏",label:"Clap"},
  {key:"heart",emoji:"❤️",label:"Love"},{key:"trophy",emoji:"🏆",label:"Trophy"},{key:"rocket",emoji:"🚀",label:"Rocket"},
];
export const REACTION_EMOJI=Object.fromEntries(REACTIONS.map(r=>[r.key,r.emoji]));
// Optimistically apply a toggle to an event object (same rules as the server's react action).
export function applyReactionToggle(ev,key){
  const reactions={...(ev.reactions||{})};
  const prev=ev.my_reaction||null;
  if(prev){reactions[prev]=Math.max(0,(reactions[prev]||1)-1);if(!reactions[prev])delete reactions[prev];}
  if(prev===key)return{...ev,reactions,my_reaction:null};
  reactions[key]=(reactions[key]||0)+1;
  return{...ev,reactions,my_reaction:key};
}
export function ReactionRow({ev,onReact,compact=false}){
  return(
    <div style={{display:"flex",gap:4,marginTop:6,flexWrap:"wrap"}}>
      {REACTIONS.map(r=>{
        const count=ev.reactions?.[r.key]||0;
        const reacted=ev.my_reaction===r.key;
        return(
          <button key={r.key} type="button" aria-label={`${r.label}${count?` (${count})`:""}`} aria-pressed={reacted}
            onClick={e=>{e.stopPropagation();Haptic.light();onReact(ev.id,r.key);}}
            style={{padding:compact?"3px 7px":"4px 8px",borderRadius:8,
              background:reacted?`${V.accent}18`:(count>0?V.inputBg:"transparent"),
              border:`1px solid ${reacted?V.accent+"40":(count>0?V.cardBorder:"transparent")}`,
              cursor:"pointer",fontSize:compact?12:14,display:"flex",alignItems:"center",gap:3,transition:"all .15s",
              transform:reacted?"scale(1.08)":"scale(1)",fontFamily:V.font}}>
            {r.emoji}{count>0&&<span style={{fontSize:11,fontWeight:700,color:reacted?V.accent:V.text3}}>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function timeAgo(ts){
  if(!ts)return"";
  const m=Math.round((Date.now()-new Date(ts))/60000);
  if(m<1)return"now";if(m<60)return`${m}m`;if(m<1440)return`${Math.round(m/60)}h`;return`${Math.round(m/1440)}d`;
}

// ─── Comment thread on a feed event ───
export const CommentIcon=({size=14,color})=>(
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z"/>
  </svg>
);
export function CommentThread({email,eventId,onCount}){
  const [comments,setComments]=useState(null);
  const [text,setText]=useState("");
  const [sending,setSending]=useState(false);
  const inputRef=useRef(null);
  useEffect(()=>{
    let alive=true;
    SocialAPI.getComments(email,eventId).then(r=>{if(alive){const list=r?.comments||[];setComments(list);onCount?.(list.length);}});
    return()=>{alive=false;};
  },[email,eventId]);
  const send=async()=>{
    const t=text.trim();if(!t||sending)return;
    setSending(true);
    const r=await SocialAPI.addComment(email,eventId,t);
    setSending(false);
    if(r?.success){
      const next=[...(comments||[]),{...r.comment,name:"You",isOwn:true}];
      setComments(next);onCount?.(next.length);setText("");
      LS.set("ft-commented-today",today());
      inputRef.current?.focus();
    } else SuccessToastCtrl.error(r?.error||"Could not post comment");
  };
  const remove=async(id)=>{
    const next=(comments||[]).filter(c=>c.id!==id);setComments(next);onCount?.(next.length);
    SocialAPI.deleteComment(email,id);
  };
  return(
    <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${V.divider}`}}>
      {comments===null&&<div style={{fontSize:11,color:V.text3,padding:"4px 0"}}>Loading comments…</div>}
      {comments?.length===0&&<div style={{fontSize:11,color:V.text3,padding:"4px 0"}}>No comments yet — say something.</div>}
      {(comments||[]).map(c=>(
        <div key={c.id} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"5px 0"}}>
          <div style={{width:24,height:24,borderRadius:12,flexShrink:0,background:`${c.isOwn?V.accent:V.purple}22`,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:c.isOwn?V.accent:V.purple}}>
            {(c.name||"?")[0]?.toUpperCase()}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"baseline",gap:6}}>
              <span style={{fontSize:12,fontWeight:700,color:V.text}}>{c.isOwn?"You":c.name}</span>
              <span style={{fontSize:11,color:V.text3}}>{timeAgo(c.created_at)}</span>
              {c.isOwn&&<button type="button" onClick={()=>remove(c.id)} aria-label="Delete comment" style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",padding:2,opacity:0.6}}>{Icons.x({size:12,color:V.text3})}</button>}
            </div>
            <div style={{fontSize:12,color:V.text2,lineHeight:1.45,wordBreak:"break-word"}}>{c.text}</div>
          </div>
        </div>
      ))}
      <div style={{display:"flex",gap:6,marginTop:6}}>
        <input ref={inputRef} value={text} maxLength={300} placeholder="Write a comment…" aria-label="Write a comment"
          onChange={e=>setText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
          style={{flex:1,minWidth:0,padding:"9px 12px",borderRadius:10,background:V.inputBg,border:`1px solid ${V.cardBorder}`,
            color:V.text,fontSize:13,outline:"none",fontFamily:V.font}}/>
        <button type="button" onClick={send} disabled={!text.trim()||sending} aria-label="Post comment"
          style={{width:38,height:38,borderRadius:10,flexShrink:0,border:"none",cursor:text.trim()?"pointer":"default",
            background:text.trim()?V.accent:V.inputBg,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={text.trim()?V.bg:V.text3} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
    </div>
  );
}

// ─── Social Load-Error Card ───
// Shown when a social screen fails to fetch. An expired/invalid session (no valid
// token) is a dead-end for "Retry" — the request just 401s again — so prompt re-auth
// instead. A genuine transient/network error keeps the retry affordance.
export function SocialErrorCard({onRetry,label="Could not load"}){
  if(!SessionManager.hasSession())return(
    <Card style={{padding:14,textAlign:"center"}}>
      <div style={{fontSize:11,color:V.danger,marginBottom:4}}>Session expired</div>
      <div style={{fontSize:11,color:V.text3,marginBottom:8,lineHeight:1.5}}>Sign in again to load this. Your data is safe on this device.</div>
      <Btn full onClick={()=>ReauthCtrl.show()}>Sign in again</Btn>
    </Card>
  );
  return(
    <Card style={{padding:14,textAlign:"center"}}>
      <div style={{fontSize:11,color:V.danger,marginBottom:6}}>{label}</div>
      <Btn v="secondary" full onClick={onRetry}>Retry</Btn>
    </Card>
  );
}

// ─── Online Status Indicator ───
export function OnlineIndicator({lastActive,size=6,showText=false}){
  if(!lastActive)return showText?<span style={{fontSize:11,color:V.text3}}>Offline</span>:null;
  const diff=Date.now()-new Date(lastActive).getTime();
  const online=diff<ONLINE_THRESHOLD_MS;
  const color=online?"#22c55e":"#6b7280";
  const label=online?"Online":diff<MS_PER_HOUR?`${Math.floor(diff/MS_PER_MINUTE)}m ago`:diff<MS_PER_DAY?`${Math.floor(diff/MS_PER_HOUR)}h ago`:"Offline";
  return(<span style={{display:"inline-flex",alignItems:"center",gap:3}}>
    <span style={{width:size,height:size,borderRadius:size,background:color,flexShrink:0}}/>
    {showText&&<span style={{fontSize:11,color:online?"#22c55e":V.text3}}>{label}</span>}
  </span>);
}

